import { type ApiProblem, call } from '@rental/contracts/client'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import {
  type Access,
  accessAfter,
  addsNothing,
  isUnlock,
  type LockReason,
  mustForgetData,
  passwordChangeAfter,
} from './access'
import { api, keeper, ProblemError } from './api'
import { forgetOpenedFiles } from './files'
import { idleMinutes, readActivity, sessionAtStart, watchIdle } from './idle'
import { rememberUnlock } from './lock-recents'
import type { StaffProblem } from './problems'
import { onResume } from './resume'
import { reportError } from './telemetry'

export const IDLE_MINUTES = idleMinutes(import.meta.env.VITE_IDLE_LOCK_MINUTES)
/**
 * CTR-2: a customer reading the terms may not touch the screen for a while,
 * so their screen waits longer than the staff lock; the grant itself ends
 * after 20 minutes whatever happens.
 */
export const SIGNING_IDLE_MINUTES = Math.max(IDLE_MINUTES, 10)

/**
 * Other tabs of the app on this device hear when it locks or turns to the
 * customer, and lock too: the session cookie they share is already over, and
 * what they show must not stay on screen until their next request.
 */
const CHANNEL = 'rental.staff.access'
type Broadcast = { type: 'locked' }

interface AccessContextValue {
  access: Access
  /** The first check could not reach the API, or read the device store: say so, try again. */
  bootProblem: StaffProblem | null
  retryBoot: () => void
  /** Moves to a new state, emptying the cache whenever the person changes. */
  transition: (next: Access) => void
  /** For screens: true when the problem moved the device (revoked, locked); show nothing more then. */
  takeOver: (problem: StaffProblem) => boolean
  lock: (reason: 'locked' | 'idle') => Promise<void>
  /** CTR-2: turn the iPad to the customer; null once turned, else what went wrong. */
  turnToCustomer: (code: string, vehicleId: string) => Promise<ApiProblem | null>
  /** The customer's screen hands the iPad back, signed or not: the lock screen follows. */
  endSigning: () => Promise<void>
}

const AccessContext = createContext<AccessContextValue | null>(null)

/**
 * A lock the server has not confirmed yet. The screen locks at once, but the
 * session cookie is httpOnly: only the API can end it. If that call failed
 * (offline when the idle lock fired), a reload must not walk back into the
 * app on the old session, so the next start ends it first.
 *
 * The iPad app keeps the flag in memory only (A1): its session lives in
 * memory too, so a new start has none left to end, and must not wait on a
 * lock it has nothing to send with.
 */
const LOCK_PENDING = 'rental.staff.lock-pending'
let lockPendingInMemory = false

function markLockPending(pending: boolean) {
  lockPendingInMemory = pending
  if (keeper) return
  try {
    if (pending) window.localStorage.setItem(LOCK_PENDING, '1')
    else window.localStorage.removeItem(LOCK_PENDING)
  } catch {
    // Storage refused: the in-memory flag still covers this page.
  }
}

function lockPending(): boolean {
  if (keeper) return lockPendingInMemory && keeper.closingSession()
  try {
    return lockPendingInMemory || window.localStorage.getItem(LOCK_PENDING) === '1'
  } catch {
    return lockPendingInMemory
  }
}

/**
 * C2: when someone last used the web app, kept across a reload. The session
 * cookie outlives the page (the server ends a session 15 minutes after its
 * last request), so a page closed or reloaded while unlocked could otherwise
 * open again on the same session without a PIN. A session found at start that
 * was left alone past the idle limit is ended instead (check()).
 *
 * Written at most every few seconds, so it can only read a little older than
 * the truth: a lock comes a few seconds early, never late. The iPad app keeps
 * none: its session lives in memory and ends with the page (A1).
 */
const LAST_ACTIVITY = 'rental.staff.last-activity'
const RECORD_EVERY_MS = 5_000
let activityRecordedAt = Number.NEGATIVE_INFINITY

function recordActivity(at: number, always = false) {
  if (keeper) return
  if (!always && at >= activityRecordedAt && at - activityRecordedAt < RECORD_EVERY_MS) return
  activityRecordedAt = at
  try {
    window.localStorage.setItem(LAST_ACTIVITY, String(Math.floor(at)))
  } catch {
    // Storage refused: the next start finds no record, and asks for the PIN.
  }
}

function lastActivity(): number | null {
  try {
    return readActivity(window.localStorage.getItem(LAST_ACTIVITY))
  } catch {
    return null
  }
}

async function endSession(): Promise<ApiProblem | null> {
  const result = await call(api.POST('/v1/staff/lock'))
  if (!result.ok) return result.problem
  markLockPending(false)
  return null
}

/**
 * A session the start could not end: stop there, never in the app. A version
 * the API refuses shows the update; the updated app ends the session first.
 */
function notEnded(problem: ApiProblem): { access: Access } | { problem: StaffProblem } {
  return accessAfter(problem)?.state === 'update' ? { access: { state: 'update' } } : { problem }
}

/** What the device is, then who is unlocked, from the server's answers only. */
async function check(): Promise<
  { access: Access; endedSession?: true } | { problem: StaffProblem }
> {
  // The iPad app: its device credential first. Unreadable is not "not enrolled".
  if (keeper && (await keeper.loadDevice()) === 'unavailable') {
    return { problem: { kind: 'client', code: 'device_store_unavailable' } }
  }
  if (lockPending()) {
    // Never trust a session the screen was locked on: end it, or stop here.
    const problem = await endSession()
    if (problem) return notEnded(problem)
  }
  const session = await call(api.GET('/v1/staff/session'))
  if (session.ok) {
    const found = keeper
      ? 'resume'
      : sessionAtStart(lastActivity(), Date.now(), IDLE_MINUTES * 60_000)
    if (found === 'resume') return { access: { state: 'unlocked', session: session.data } }
    // C2: left alone past the limit, or no telling how long, then reloaded or
    // opened again: the session ends as the idle lock would have ended it, and
    // the PIN is asked. The lock screen says why only when it knows.
    markLockPending(true)
    const problem = await endSession()
    if (problem) return notEnded(problem)
    return {
      access: { state: 'locked', reason: found === 'idle' ? 'idle' : 'locked' },
      endedSession: true,
    }
  }
  const after = accessAfter(session.problem)
  if (!after) return { problem: session.problem }
  if (after.state !== 'locked') return { access: after }
  // No session: is the iPad turned to a customer who has not signed yet (a reload)?
  const signing = await call(api.GET('/v1/staff/signing'))
  if (signing.ok) {
    return {
      access: {
        state: 'signing',
        contractId: signing.data.contractId,
        expiresAt: signing.data.expiresAt,
      },
    }
  }
  const signingAfter = accessAfter(signing.problem)
  if (signingAfter && signingAfter.state !== 'locked') return { access: signingAfter }
  // No session: is this an enrolled device (the lock screen) or not (enrollment)?
  const device = await call(api.GET('/v1/staff/device'))
  if (device.ok) return { access: { state: 'locked', reason: 'locked' } }
  const deviceAfter = accessAfter(device.problem)
  return deviceAfter ? { access: deviceAfter } : { problem: device.problem }
}

export function AccessProvider({ children }: { children: ReactNode }) {
  const [access, setAccess] = useState<Access>({ state: 'checking' })
  const [bootProblem, setBootProblem] = useState<StaffProblem | null>(null)
  const current = useRef<Access>(access)
  const onProblem = useRef<(problem: ApiProblem) => boolean>(() => false)

  const [queryClient] = useState(() => {
    const report = (error: unknown) => {
      if (error instanceof ProblemError) onProblem.current(error.problem)
      // S2: anything else is a bug: reported, and shown as `unexpected` (problems.ts).
      else reportError(error, { mechanism: 'react-query', handled: true })
    }
    return new QueryClient({
      queryCache: new QueryCache({ onError: report }),
      mutationCache: new MutationCache({ onError: report }),
      defaultOptions: {
        queries: {
          // An API answer is final; only a dropped connection is worth a second try.
          retry: (failures, error) =>
            failures < 2 && error instanceof ProblemError && error.problem.kind === 'network',
          staleTime: 30_000,
        },
      },
    })
  })

  const transition = useCallback(
    (next: Access) => {
      if (mustForgetData(current.current, next)) {
        queryClient.clear()
        // A2: and a PDF the iPad app wrote for its viewer (native/pdf-viewer.ts).
        void forgetOpenedFiles()
      }
      // A new session replaces the old one on the server: nothing left to end.
      // Unlocking is using the app: the idle limit counts from here (C2). Only an
      // unlock: the same person's session told something new (a PIN saved, S9's
      // password to change, heard from a refresh nobody pressed) is not activity.
      if (next.state === 'unlocked' && isUnlock(current.current, next)) {
        markLockPending(false)
        recordActivity(Date.now(), true)
        // UI-2a: the lock screen offers the last three to unlock this iPad first.
        rememberUnlock(next.session.user.id)
      }
      // C2: an app the API refuses cannot end its session now. The updated one
      // ends it before anything else, rather than resume it without a PIN.
      if (current.current.state === 'unlocked' && next.state === 'update') markLockPending(true)
      // The iPad app's credentials move with the screen, before anything else can ask.
      keeper?.follow(current.current, next)
      current.current = next
      // At once, not at React's next render: until the screens behind the lock
      // unmount, a timer of theirs (the Today board's minute, Q-79) could ask
      // again and refill the cache just emptied.
      flushSync(() => setAccess(next))
    },
    [queryClient],
  )

  const takeOver = useCallback(
    (problem: StaffProblem) => {
      // The app's own problems never move the device: they belong to the screen.
      if (problem.kind === 'client') return false
      const next = accessAfter(problem) ?? passwordChangeAfter(current.current, problem)
      if (!next) return false
      if (!addsNothing(current.current, next)) transition(next)
      return true
    },
    [transition],
  )
  onProblem.current = takeOver

  const channel = useRef<BroadcastChannel | null>(null)
  const tellOtherTabs = useCallback(() => {
    channel.current?.postMessage({ type: 'locked' } satisfies Broadcast)
  }, [])

  const booting = useRef(false)
  const boot = useCallback(async () => {
    // One check at a time: a press on "try again" and a resume can come together.
    if (booting.current) return
    booting.current = true
    setBootProblem(null)
    try {
      const result = await check()
      if ('access' in result) {
        transition(result.access)
        // C2: the session it ended is the one the other tabs show.
        if (result.endedSession) tellOtherTabs()
      } else setBootProblem(result.problem)
    } finally {
      booting.current = false
    }
  }, [transition, tellOtherTabs])

  useEffect(() => {
    void boot()
  }, [boot])

  // A2: a device store the iPad could not read while it was locked: once the
  // app is back in front of someone, the iPad is unlocked; read it again.
  const storeUnavailable =
    bootProblem?.kind === 'client' && bootProblem.code === 'device_store_unavailable'
  useEffect(() => {
    if (!storeUnavailable) return
    return onResume(() => void boot())
  }, [storeUnavailable, boot])

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return
    const opened = new BroadcastChannel(CHANNEL)
    channel.current = opened
    opened.onmessage = (event: MessageEvent<Broadcast>) => {
      // Another tab locked or turned: this one stops showing what it showed.
      if (event.data?.type === 'locked' && current.current.state === 'unlocked') {
        transition({ state: 'locked', reason: 'locked' })
      }
    }
    return () => {
      opened.close()
      channel.current = null
    }
  }, [transition])

  const lock = useCallback(
    async (reason: 'locked' | 'idle') => {
      markLockPending(true)
      transition({ state: 'locked', reason })
      tellOtherTabs()
      await endSession()
    },
    [transition, tellOtherTabs],
  )

  const turnToCustomer = useCallback(
    async (code: string, vehicleId: string): Promise<ApiProblem | null> => {
      const turned = await call(
        api.POST('/v1/staff/bookings/{code}/contract/turn', {
          params: { path: { code } },
          body: { vehicleId },
        }),
      )
      if (!turned.ok) {
        if (takeOver(turned.problem)) return null
        return turned.problem
      }
      // The server ended the session with the turn; the cache goes with it.
      transition({
        state: 'signing',
        contractId: turned.data.contractId,
        expiresAt: turned.data.expiresAt,
      })
      tellOtherTabs()
      return null
    },
    [transition, takeOver, tellOtherTabs],
  )

  const endSigning = useCallback(async () => {
    const reason: LockReason = 'handback'
    transition({ state: 'locked', reason })
    // Best effort: an unreachable server still lets the grant expire on its own.
    await call(api.POST('/v1/staff/signing/end'))
  }, [transition])

  // ARCHITECTURE: auto-lock after a few minutes without activity, while unlocked.
  const unlocked = access.state === 'unlocked'
  useEffect(() => {
    if (!unlocked) return
    const watch = watchIdle(IDLE_MINUTES * 60_000, () => void lock('idle'))
    const activity = () => {
      watch.activity()
      recordActivity(Date.now())
    }
    // Heard on the way down, before any screen: one that stops an event
    // from going further must not make someone at work look idle.
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const
    for (const event of events) {
      window.addEventListener(event, activity, { capture: true, passive: true })
    }
    // An iPad that slept, or an app left in the background, past the limit: lock now.
    const stopResume = onResume(() => watch.check())
    return () => {
      watch.stop()
      for (const event of events) window.removeEventListener(event, activity, { capture: true })
      stopResume()
    }
  }, [unlocked, lock])

  // CTR-2: the customer's screen closes when left alone, and when the grant runs out.
  const signing = access.state === 'signing' ? access.expiresAt : null
  useEffect(() => {
    if (!signing) return
    const watch = watchIdle(SIGNING_IDLE_MINUTES * 60_000, () => void endSigning())
    const left = Math.max(0, Date.parse(signing) - Date.now())
    const expiry = window.setTimeout(() => void endSigning(), left)
    const activity = () => watch.activity()
    const events = [
      'pointerdown',
      'pointermove',
      'keydown',
      'wheel',
      'touchstart',
      'scroll',
    ] as const
    for (const event of events) {
      window.addEventListener(event, activity, { capture: true, passive: true })
    }
    // A2: back from the background, the screen looks again at once, as it
    // would have if its timers had kept running: left too long, or the grant
    // ran out while the app was away.
    const stopResume = onResume(() => {
      if (Date.now() >= Date.parse(signing)) void endSigning()
      else watch.check()
    })
    return () => {
      watch.stop()
      window.clearTimeout(expiry)
      for (const event of events) window.removeEventListener(event, activity, { capture: true })
      stopResume()
    }
  }, [signing, endSigning])

  const value = useMemo<AccessContextValue>(
    () => ({
      access,
      bootProblem,
      retryBoot: () => void boot(),
      transition,
      takeOver,
      lock,
      turnToCustomer,
      endSigning,
    }),
    [access, bootProblem, boot, transition, takeOver, lock, turnToCustomer, endSigning],
  )
  return (
    <AccessContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </AccessContext.Provider>
  )
}

export function useAccess(): AccessContextValue {
  const value = useContext(AccessContext)
  if (!value) throw new Error('useAccess outside AccessProvider')
  return value
}

/** The unlocked session, for screens that only exist once someone is unlocked. */
export function useSession() {
  const { access } = useAccess()
  if (access.state !== 'unlocked') throw new Error('useSession while not unlocked')
  return access.session
}
